import { useEffect, useState } from "react";
import { Alert, Col, Container, ListGroup, Row } from "react-bootstrap";
import { Eye } from "react-feather";
import { Helmet } from "react-helmet-async";
import toast from "react-hot-toast";

import Loader from "../../components/Loader";
import ShipmentRackList from "./components/createShipment/ShipmentRackList";
import ShipmentRacks from "./components/createShipment/ShipmentRacks";

import shipmentService from "../../services/shipmentService";
import utilsService from "../../services/utils.service";

const CreateShipmentPage = () => {
  const [initializing, setInitializing] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [listingAccounts, setListingAccounts] = useState();
  const [shipmentCategories, setShipmentCategories] = useState([]);

  const [selectedAccount, setSelectedAccount] = useState();

  const [shipmentRacks, setShipmentRacks] = useState([]);
  const [selectedShipmentRacks, setSelectedShipmentRacks] = useState([]);

  const [shipmentPlanOptions, setShipmentPlanOptions] = useState();
  const [selectedShipmentPlans, setSelectedShipmentPlans] = useState([]);
  const [activeShipmentCategory, setActiveShipmentCategory] = useState(1);

  useEffect(() => {
    initialize();
  }, []);

  useEffect(() => {
    setSelectedShipmentRacks([]);
    setActiveShipmentCategory();
  }, [selectedAccount]);

  const initialize = async () => {
    setInitializing(true);

    // Get the listing accounts
    const result = await utilsService.getListingAccounts({
      amazonOnly: true,
    });

    // Get the shipment categories
    const result2 = await shipmentService.getShipmentCategories();

    if (!result.success || !result2.success) {
      toast.error("Something went wrong. Please try again.");
      setInitializing(false);
      return;
    }

    setListingAccounts(result.listingAccounts);
    setShipmentCategories(result2.shipmentCategories);
    setInitializing(false);
  };

  const getShipmentRacks = async ({
    shipmentRackId,
    listingAccountId,
    shipmentRackStatusId,
    shipmentCategoryId,
    lastUpdatedStart,
    lastUpdatedEnd,
    globalSearch,
    pageIndex,
    pageSize,
    sortBy,
  }) => {
    const result = await shipmentService.getShipmentRacks({
      shipmentRackId,
      listingAccountId,
      shipmentRackStatusId,
      shipmentCategoryId,
      lastUpdatedStart,
      lastUpdatedEnd,
      globalSearch,
      pageIndex,
      pageSize,
      sortBy,
    });

    return result;
  };

  const createShipmentPlans = async () => {
    setSubmitting(true);

    const shipmentRackIds = selectedShipmentRacks.map(
      (shipmentRack) => shipmentRack.shipmentRackId
    );
    const listingAccountId = selectedAccount.listingAccountId;

    const result = await shipmentService.createShipmentPlans({
      listingAccountId,
      shipmentRackIds,
      shipmentCategoryId: activeShipmentCategory,
    });

    if (result.success) {
      toast.success("Shipment plans have been created successfully!", {
        duration: 5000,
      });

      setShipmentPlanOptions(result.shipmentPlansResult.createdShipmentPlans);
      setSubmitting(false);
    } else {
      if (result?.shipmentPlansResult?.noItems) {
        toast.error("There are no items on the selected rack/s", {
          duration: 5000,
        });
      } else {
        toast.error("Something went wrong. Please try again.", {
          duration: 5000,
        });
      }

      setSubmitting(false);
    }
  };

  const createShipments = async () => {
    setSubmitting(true);
    const shipmentIds = selectedShipmentPlans.map(
      (shipmentPlan) => shipmentPlan.shipmentId
    );
    const shipmentRackIds = selectedShipmentRacks.map(
      (shipmentRack) => shipmentRack.shipmentRackId
    );

    const result = await shipmentService.createShipments({
      shipmentIds,
      shipmentRackIds,
    });

    if (result.success) {
      setSelectedShipmentPlans([]);
      setSelectedShipmentRacks([]);
      toast.success("Shipments have been created successfully!");
      setTimeout(() => {
        window.location.reload(false);
      }, 2500);
    } else {
      console.log(result);
      toast.error(result.message, {
        duration: 10000,
      });
    }

    setSubmitting(false);
  };

  return (
    <>
      <Helmet title="Create Shipment" />
      <h1 className="mb-3">Create Shipment</h1>
      <Container fluid className="p-0">
        {initializing ? (
          <Loader />
        ) : (
          <>
            <Row className="mb-3">
              <Col xxl={8}>
                <ListGroup horizontal>
                  {listingAccounts.map((account, index) => {
                    return (
                      <ListGroup.Item
                        tag="a"
                        key={index}
                        className="text-center"
                        onClick={() => setSelectedAccount(account)}
                        action
                        active={selectedAccount === account}
                        disabled={submitting}
                      >
                        {account.listingAccount}
                      </ListGroup.Item>
                    );
                  })}
                </ListGroup>
              </Col>
            </Row>
            {!selectedAccount ? (
              <Row>
                <Col xxl={8}>
                  <Alert variant="primary">
                    <div className="alert-icon">
                      <Eye
                        className="position-relative top-50 start-50 translate-middle"
                        size={20}
                      />
                    </div>
                    <div className="alert-message">
                      Select a <strong>listing account</strong> to view shipment
                      racks.
                    </div>
                  </Alert>
                </Col>
              </Row>
            ) : (
              <Row className="mb-3">
                <Col xxl={8}>
                  <ShipmentRacks
                    key={selectedAccount.listingAccountId}
                    selectedAccount={selectedAccount}
                    shipmentCategories={shipmentCategories}
                    shipmentRacks={shipmentRacks}
                    setShipmentRacks={setShipmentRacks}
                    getShipmentRacks={getShipmentRacks}
                    setSelectedShipmentRacks={setSelectedShipmentRacks}
                    submitting={submitting}
                    activeShipmentCategory={activeShipmentCategory}
                    setActiveShipmentCategory={setActiveShipmentCategory}
                  />
                </Col>
                {shipmentRacks.length > 0 && activeShipmentCategory && (
                  <Col xxl={4}>
                    <ShipmentRackList // Sticky Sidebar
                      selectedShipmentRacks={selectedShipmentRacks}
                      createShipmentPlans={createShipmentPlans}
                      shipmentPlanOptions={shipmentPlanOptions}
                      submitting={submitting}
                      setSelectedShipmentPlans={setSelectedShipmentPlans}
                      selectedShipmentPlans={selectedShipmentPlans}
                      createShipments={createShipments}
                      activeShipmentCategory={activeShipmentCategory}
                    />
                  </Col>
                )}
              </Row>
            )}
          </>
        )}
      </Container>
    </>
  );
};

export default CreateShipmentPage;
